<script setup lang="ts">
import IconX from '~icons/tabler/x';

defineOptions({name: 'BaseAnnouncementBar'});

withDefaults(defineProps<{
    variant?: 'error' | 'success' | 'info';
    /**
     * Hides the close button when false — use for a bar the user must not be able
     * to dismiss because the condition it reports is still unresolved.
     */
    dismissible?: boolean;
    closeAriaLabel?: string;
}>(), {
    variant: 'error',
    dismissible: true,
    closeAriaLabel: 'Sulge',
});

defineEmits<{
    (e: 'dismiss'): void;
}>();
</script>

<template>
    <!-- alert for errors so screen readers interrupt; status for success, which can wait. -->
    <div
        class="merit-announcement-bar"
        :class="`merit-announcement-bar--${variant}`"
        :role="variant === 'error' ? 'alert' : 'status'"
    >
        <p class="merit-announcement-bar__text">
            <slot />
        </p>
        <button
            v-if="dismissible"
            type="button"
            class="merit-announcement-bar__close"
            :aria-label="closeAriaLabel"
            data-test="announcement-bar-close"
            @click="$emit('dismiss')"
        >
            <IconX aria-hidden="true" />
        </button>
    </div>
</template>

<style scoped>
/* merit- prefix: legacy Palk CSS claims plenty of generic names, and a bare .announcement-bar
   could inherit properties this component never redeclares. */
.merit-announcement-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 56px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #292929;
    text-align: center;
}

.merit-announcement-bar--error {
    background: #FFDFDF;
}

.merit-announcement-bar--success {
    background: #DFFFE1;
}

/* A chosen stand-in, not a Figma fill: the design has no neutral state colour, and the blue this
   used to carry read as information rather than as a state still resolving. */
.merit-announcement-bar--info {
    background: #E1E4ED;
}

.merit-announcement-bar__text {
    margin: 0;
}

/* Slot content belongs to the parent's scope, so :slotted is the only way to reach a link the
   consumer passes in. Keeps the design's link treatment without every caller restating it.

   A button as well as an anchor: navigating inside the program is a click, not an href, and a
   button is what gives that the keyboard and screen-reader behaviour an anchor without a target
   would lose. */
.merit-announcement-bar__text :slotted(a),
.merit-announcement-bar__text :slotted(button) {
    color: #294AFF;
    text-decoration: underline;
    cursor: pointer;
}

.merit-announcement-bar__text :slotted(button) {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
}

.merit-announcement-bar__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: #919191;
}

.merit-announcement-bar__close:hover {
    color: #292929;
}

.merit-announcement-bar__close svg {
    display: block;
    width: 18px;
    height: 18px;
}
</style>
